import React, { useState } from "react";
import { FileText, X, ShieldCheck } from "lucide-react";
import { getI18n } from "../lib/i18n";

const PALETTE = {
  primary: "#7B5CE0",
  primaryDark: "#5A3FC0",
  text: "#2A1F4A",
  textSoft: "#6E6688",
  border: "#E8E1FA",
};

/**
 * Modal that displays the Terms & Conditions. Pages render it stand-alone
 * (e.g. linked from the footer) or in tandem with <TermsCheckbox/>.
 */
export function TermsModal({ language = "it", open, onClose }) {
  const t = getI18n(language).terms;
  if (!open) return null;
  return (
    <div
      data-testid="terms-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9998] grid place-items-center px-4 py-8"
      style={{ background: "rgba(8,4,24,0.7)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-2xl max-h-[85vh] rounded-3xl flex flex-col"
           style={{ background: "white", border: `1px solid ${PALETTE.border}` }}>
        <div className="flex items-center justify-between px-6 py-4 border-b"
             style={{ borderColor: PALETTE.border }}>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl grid place-items-center"
                 style={{ background: `${PALETTE.primary}1A` }}>
              <FileText className="h-4 w-4" style={{ color: PALETTE.primary }} />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: PALETTE.text }}>
              {t.modal_title}
            </h2>
          </div>
          <button
            data-testid="terms-modal-close"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-lg"
            style={{ color: PALETTE.primaryDark }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto space-y-5">
          {t.sections.map((s, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold mb-1.5" style={{ color: PALETTE.primaryDark }}>
                {i + 1}. {s.title}
              </h3>
              <p className="text-[13px] leading-relaxed" style={{ color: PALETTE.text }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
        <div className="px-6 py-3.5 border-t flex justify-end"
             style={{ borderColor: PALETTE.border, background: "rgba(248,244,255,0.5)" }}>
          <button
            onClick={onClose}
            data-testid="terms-modal-close-bottom"
            className="px-4 py-2 rounded-xl text-sm font-medium"
            style={{ background: PALETTE.primary, color: "white" }}
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Checkbox + label + clickable "Terms" link. Designed to be placed above
 * every Stripe checkout CTA. Controlled component: the parent owns `accepted`.
 */
export function TermsCheckbox({ language = "it", accepted, onChange, testId = "terms-accept-checkbox" }) {
  const t = getI18n(language).terms;
  const [showTerms, setShowTerms] = useState(false);

  return (
    <>
      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid={testId}
          checked={!!accepted}
          onChange={(e) => onChange?.(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded shrink-0"
          style={{ accentColor: PALETTE.primary }}
        />
        <span className="text-[12.5px] leading-snug" style={{ color: PALETTE.text }}>
          {t.checkbox}{" "}
          <button
            type="button"
            data-testid="terms-open-link"
            onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
            className="underline font-medium"
            style={{ color: PALETTE.primaryDark }}
          >
            {t.cta_open}
          </button>
        </span>
      </label>
      <TermsModal language={language} open={showTerms} onClose={() => setShowTerms(false)} />
    </>
  );
}

export default TermsModal;
